import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUpload } from 'react-icons/fa';
import * as icons from "../../../../components/fileTypesIcons.js";
import folderSearch from '../../../../assets/icon/folder-search.svg';

/**
 * FileUploadUI Component
 * 
 * Provides a drag-and-drop file upload interface for form questions.
 * Displays either a selected file with remove option or a drop zone for new uploads.
 * Supports both embedded (compact) and full-size display modes.
 * 
 * @param {string} questionId - Unique identifier for the question
 * @param {object} answerFiles - Object containing uploaded files by question ID
 * @param {function} setAnswerFiles - Function to update the answerFiles state
 * @param {function} handleInputChange - Function to handle input changes
 * @param {function} selectAnswerFile - Function to open file selection modal
 * @param {function} handleFileOpen - Function to open/view files
 * @param {function} handleFileDownload - Function to download files
 * @param {boolean} isEmbedded - Whether to display in compact mode
 * @param {boolean} isSimulation - Whether component is in simulation mode
 * @param {boolean} readOnly - Whether the component is in read-only mode
 * @param {object} dragAnimationRef - Reference for drag animation state
 * @param {function} _frDraggingEffect - Function to handle drag visual effects
 * @param {function} hideDragBubble - Function to hide drag animation bubble
 * @param {object} uploadManager - Upload manager utility object
 * @param {string} answersFolderId - ID of the folder where answer files are stored
 * @param {function} currentUser - Function to get current user information
 * @param {object} MySwal - SweetAlert2 instance for user notifications
 * @param {object} colors - Color scheme object for styling
 * @param {function} t - Translation function
 */
const FileUploadUI = ({
  questionId,
  answerFiles,
  setAnswerFiles,
  handleInputChange,
  selectAnswerFile,
  handleFileOpen,
  handleFileDownload,
  isEmbedded = false,
  isSimulation = false,
  readOnly = false,
  dragAnimationRef,
  _frDraggingEffect,
  hideDragBubble,
  uploadManager,
  answersFolderId,
  currentUser,
  MySwal,
  colors,
  t
}) => {
  // Check if a file is already uploaded for this question
  const hasFile = answerFiles[questionId];
  
  // Generate unique CSS class prefix based on mode (simulation or compilation)
  const dragClassPrefix = isSimulation ? 'sim' : 'comp';
  
  /**
   * Cleans up drag highlighting effects for a specific drop area
   * Removes visual feedback when drag operation ends
   * 
   * @param {string} id - The question ID to clean up highlighting for
   */
  const cleanupDragHighlight = (id) => {
    window.$(`#${dragClassPrefix}-drop-area-${id}`).removeClass(`${dragClassPrefix}-draggable-area`);
    hideDragBubble();
  };
  
  // If a file is already uploaded, display file information with action buttons
  if (hasFile) {
    const file = answerFiles[questionId];
    const fileIcon = icons.iconMapping(file.MimeType);
    
    return (
      <div className={isEmbedded ? "mt-3 mb-0" : "mt-3 mb-0"}>
        <div className="card">
          <div className="card-body p-3">
            <div className="d-flex align-items-center">
              <img src={fileIcon} alt="File icon" className="me-2" style={{ width: "24px", height: "24px" }} />
              <div className="flex-grow-1">
                <h6 className="mb-0">{file.Name}</h6>
                <small className="text-muted">
                  {file.Size ? `${Math.round(file.Size / 1024)} KB` : ''}
                </small>
              </div>
              <div className="btn-group ms-2">
                {/* Open/View file button */}
                {handleFileOpen && (
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleFileOpen(file)}
                    title={t("openFile", { ns: "labels", defaultValue: "Open File" })}
                  >
                    <i className="fa-solid fa-eye"></i>
                  </button>
                )}
                
                {/* Download file button */}
                {handleFileDownload && (
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleFileDownload(file)}
                    title={t("downloadFile", { ns: "labels", defaultValue: "Download File" })}
                  >
                    <i className="fa-solid fa-download"></i>
                  </button>
                )}
                
                {/* Remove file button - only in edit mode */}
                {!readOnly && (
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      // Remove the uploaded file from state
                      setAnswerFiles(prev => {
                        const newFiles = {...prev};
                        delete newFiles[questionId];
                        return newFiles;
                      });
                    }}
                    title={t("removeFile", { ns: "labels", defaultValue: "Remove File" })}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Display drop zone for file upload
  return (
    <div className={isEmbedded ? "mt-3 mb-0" : "mt-3 mb-0"} style={{ width: '100%' }}>
      <div className="d-flex flex-column">
        {/* Upload instructions and browse button */}
        <div className="d-flex align-items-center mb-2">
          <small className="text-muted flex-grow-1">
            {t('dragFilesHere', { ns: 'labels', defaultValue: 'Drag files here' })} {t('orClickToSelect', { ns: 'labels' })}
          </small>
          {!readOnly && (
            <img 
              src={folderSearch} 
              className='ms-2' 
              title={t('chooseFiles', { ns: 'labels' })} 
              style={{ cursor: "pointer", width: "22px", height: "22px" }} 
              onClick={() => selectAnswerFile(questionId)} 
            />
          )}
        </div>
        
        {/* Drag and drop area */}
        <div 
          className={`${dragClassPrefix}-draggable-container`}
          style={{ 
            width: '100%',
            minHeight: isEmbedded ? '70px' : '90px',
            cursor: readOnly ? 'default' : 'pointer'
          }}
          onClick={() => !readOnly && selectAnswerFile(questionId)}
          onDragOver={(e) => {
            if (readOnly) return;
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseOut={(e) => {
            if (readOnly) return;
            window.$(`#${dragClassPrefix}-drop-area-${questionId}`).removeClass(`${dragClassPrefix}-draggable-area`);
          }}
          onDragEnter={(e) => {
            if (readOnly) return;
            e.preventDefault();
            e.stopPropagation();
            window.$(`#${dragClassPrefix}-drop-area-${questionId}`).addClass(`${dragClassPrefix}-draggable-area`);
            _frDraggingEffect(e, true, questionId);
          }}
          onDragEnd={(e) => {
            if (readOnly) return;
            e.preventDefault();
            e.stopPropagation();
            cleanupDragHighlight(questionId);
          }}
          onDragLeave={(e) => {
            if (readOnly) return;
            e.preventDefault();
            e.stopPropagation();
            
            // Only remove highlight if we're actually leaving the element
            // and not just entering a child element
            if (!e.currentTarget.contains(e.relatedTarget)) {
              window.$(`#${dragClassPrefix}-drop-area-${questionId}`).removeClass(`${dragClassPrefix}-draggable-area`);
            }
          }}
          onDrop={async (e) => {
            if (readOnly) return;
            
            cleanupDragHighlight(questionId);
            e.preventDefault();
            e.stopPropagation();
            
            try {
              // Extract files from the drag and drop event
              const _files = await uploadManager.getAllFileEntries(e.dataTransfer.items);
              
              if (_files.files && _files.files.length) {
                // Validate that answers folder is available
                if (!answersFolderId) {
                  MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('failedToRetrieveFiles', { ns: 'labels' }),
                    icon: 'error',
                    confirmButtonColor: colors?.danger || '#dc3545'
                  });
                  return;
                }
                
                // Show upload progress indicator
                MySwal.fire({
                  title: t('uploading', { ns: 'labels', defaultValue: 'Uploading...' }),
                  text: _files.files[0].name,
                  allowOutsideClick: false,
                  showConfirmButton: false,
                  willOpen: () => {
                    MySwal.showLoading();
                  }
                });
                
                // Execute file upload using upload manager
                uploadManager.fileUpload({
                  user: currentUser(),
                  parentId: answersFolderId,
                  files: _files.files,
                  onFileAdded: (files) => {
                    if (Array.isArray(files) && files.length > 0) {
                      // Use the first uploaded file (in case multiple files were dropped)
                      const file = files[0];
                      
                      // Update the answer files state with the uploaded file
                      setAnswerFiles(prev => ({
                        ...prev,
                        [questionId]: file
                      }));
                      
                      // Show success notification
                      MySwal.fire({
                        title: t('fileUploadSuccessful', { ns: 'labels' }),
                        text: file.Name,
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                      });
                    }
                  },
                  onError: (error) => {
                    console.error("Upload error:", error);
                    MySwal.fire({
                      title: t('error', { ns: 'labels' }),
                      text: error.message || t('failedToRetrieveFiles', { ns: 'labels' }),
                      icon: 'error',
                      confirmButtonColor: colors?.danger || '#dc3545'
                    });
                  }
                });
              }
            } catch (error) {
              console.error("Error processing dropped files:", error);
              MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('failedToRetrieveFiles', { ns: 'labels' }),
                icon: 'error',
                confirmButtonColor: colors?.danger || '#dc3545'
              });
            }
          }}
          id={`${dragClassPrefix}-drop-area-${questionId}`}
        >
          {/* Drop zone visual content */}
          <div className="text-center">
            <FaUpload className="mb-2" size={isEmbedded ? 16 : 20} color="#6c757d" />
            <div className="small text-muted">
              {readOnly ? t('readOnlyMode', { ns: 'labels', defaultValue: 'Read-only mode' }) : t('dropFileHere', { ns: 'labels' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadUI; 